import retry from 'async-retry';

import type { EntityRepositoryBase } from './entityRepositoryBase';
import type {
  EntityDataPubType,
  EntityName,
  CreateEntityMutation,
  UpdateEntityMutation,
  DeleteEntityMutation,
  GetEntityQuery,
  GetEntitiesQuery,
  GetEntitiesWithKeyQuery,
  GetEntitiesWithSubKeyQuery,
  GetDataEntitiesQuery,
  GetDataEntitiesWithKeyQuery,
  GetDataEntitiesWithSubKeyQuery,
  GetDataEntityQuery,
  GetPubEntityQuery,
  GetPubEntitiesQuery,
  GetPubEntitiesWithKeyQuery,
  GetPubEntitiesWithSubKeyQuery,
  GetPublicEntitiesQuery,
  GetPublicEntityQuery,
  GetPublicEntitiesWithKeyQuery,
  GetPublicEntitiesWithSubKeyQuery,
} from '@/infra/appsync';
import type { CognitoIApi } from '@/infra/cognitoIApi';

import { retries } from '@/constants/env';
import { ConditionalRequestFailedError } from '@/errors/conditionalRequestFailedError';
import { SessionError } from '@/errors/sessionError';
import {
  getSdk,
} from '@/infra/appsync';
import { api } from '@/share';

/**
 * AsyncRetryエラー内容返却用
 */
export type AsyncRetryErrorType = {
  name: string,
  error: string,
}

/**
 * エラーハンドリング用関数
 * @param errorObj エラー内容
 * @returns エラー
 */
export const handleError = (errorObj: AsyncRetryErrorType) => {
  if (errorObj.name === 'SessionError') {
    return new SessionError(errorObj.error);
  }
  if (errorObj.name === 'ConditionalRequestFailedError') {
    throw new ConditionalRequestFailedError(errorObj.error);
  }
  return new Error(errorObj.error);
};

/**
 * AsyncRetryの処理中断条件を記載するためのラッパー
 * @param func 関数
 * @returns 結果、エラー内容
 */
const funcWrap = async <T>(func: () => Promise<T>) => {
  try {
    const response = await func();
    return {
      response,
      errorObj: undefined,
    };
  } catch (e) {
    const error = e instanceof Error ? e.stack || e.message : String(e);
    if (error.includes('"errorType":"UnauthorizedException"')) {
      const errorObj: AsyncRetryErrorType = {
        name: 'SessionError',
        error,
      };
      return {
        response: undefined,
        errorObj,
      };
    }
    if (error.includes('The conditional request failed')) {
      const errorObj: AsyncRetryErrorType = {
        name: 'ConditionalRequestFailedError',
        error,
      };
      return {
        response: undefined,
        errorObj,
      };
    }
    throw e;
  }
};

/**
 * Entityの抽象クラス.
 * data/pub両方取得.
 */
export abstract class AppsyncRepositoryBase<E, P, J, PP, JP> {
  /**
   * Cognito API
   */
  cognitoApi: CognitoIApi;

  constructor(cognitoApi: CognitoIApi) {
    this.cognitoApi = cognitoApi;
  }

  /**
   * 全データ取得
   * @param getData 取得方法
   * @returns 項目リスト
   */
  protected async getAllData(
    getData: (token: string | null) => Promise<{
      /** 次のトークン */
      nextToken: string,
      /** アイテムリスト */
      items: E[],
    }>,
  ) {
    let error = '';
    for (let i = 0; i < retries; i += 1) {
      try {
        let nextToken: string | null = null;
        const items: E[] = [];
        while (nextToken !== '') {
          const result: {
            nextToken: string;
            items: E[];
          } = await getData(nextToken);
          nextToken = result.nextToken;
          for (let i = 0; i < result.items.length; i += 1) {
            const item: E = result.items[i];
            items.push(item);
          }
        }
        return items;
      } catch (e) {
        error = e instanceof Error ? e.stack || e.message : String(e);
      }
    }
    throw new Error(`getAllData: ${error}`);
  }

  /**
   * 作成
   * @param entity Entity
   * @returns 結果
   */
  async create(entity: EntityRepositoryBase<P, J, PP, JP>) {
    const input = entity.toCreateEntityInput();
    const result = await this.retryCreateEntity(async () => {
      const client = await this.cognitoApi.getGraphQLClient();
      const sdk = getSdk(client);
      const response = await sdk.CreateEntity({ input });
      return response;
    }, this.entityName);
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.createEntity;
    if (!resultEntity) {
      throw Error('Error in ChildRepository/create: not found property');
    }
    return this.createFromEntityType(resultEntity);
  }

  /**
   * 更新
   * @param entity Entity
   * @returns 結果
   */
  async update(entity: EntityRepositoryBase<P, J, PP, JP>) {
    const input = entity.toUpdateEntityInput();
    const result = await this.retryUpdateEntity(async () => {
      const client = await this.cognitoApi.getGraphQLClient();
      const sdk = getSdk(client);
      const response = await sdk.UpdateEntity({ input });
      return response;
    }, this.entityName);
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.updateEntity;
    if (!resultEntity) {
      throw Error(`Error in ${this.entityName}/update: not found property`);
    }
    return this.createFromEntityType(resultEntity);
  }

  /**
   * 削除
   * @param entity Entity
   * @returns 結果
   */
  async delete(entity: EntityRepositoryBase<P, J, PP, JP>) {
    const input = entity.toDeleteEntityInput();
    const result = await this.retryDeleteEntity(async () => {
      const client = await this.cognitoApi.getGraphQLClient();
      const sdk = getSdk(client);
      const response = await sdk.DeleteEntity({ input });
      return response;
    }, this.entityName);
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.deleteEntity;
    if (!resultEntity) {
      throw Error(`Error in ${this.entityName}/delete: not found property`);
    }
    return true;
  }

  /**
   * 取得
   * @param id ID
   * @returns 結果
   */
  async get(id: string) {
    const result = await this.retryGetEntity(
      async () => {
        const client = await this.cognitoApi.getGraphQLClient();
        const sdk = getSdk(client);
        const response = await sdk.GetEntity({
          entityName: this.entityName,
          entityKey: id,
        });
        return response;
      },
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntity;
    if (!resultEntity) {
      throw Error(`Error in ${this.entityName}/get: not found property`);
    }
    return this.createFromEntityType(resultEntity);
  }

  /**
   * リスト取得
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns データ
   */
  async getList(nextToken: string | null, limit = 25) {
    const result = await this.retryGetEntities(
      async () => {
        const client = await this.cognitoApi.getGraphQLClient();
        const sdk = getSdk(client);
        const response = await sdk.GetEntities({
          entityName: this.entityName,
          limit,
          nextToken,
        });
        return response;
      },
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntities;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getList: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityType(v),
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * 全リストを取得
   * @param limit 上限
   * @returns 全リスト
   */
  async getAllList(limit = 25) {
    const items = this.getAllData(async (token: string | null) => {
      const items = await this.getList(token, limit);
      return items;
    });
    return items;
  }

  /**
   * キーでリストを取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns リスト
   */
  async getListWithKey(key: string, nextToken: string | null, limit = 25) {
    const result = await this.retryGetEntitiesWithKey(
      async () => {
        const client = await this.cognitoApi.getGraphQLClient();
        const sdk = getSdk(client);
        const response = await sdk.GetEntitiesWithKey({
          entityName: this.entityName,
          key,
          limit,
          nextToken,
        });
        return response;
      },
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getListWithKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityType(v),
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * キーで全リストを取得
   * @param key キー
   * @param limit 上限
   * @returns リスト
   */
  async getAllListWithKey(key: string, limit = 25) {
    const items = this.getAllData(async (token: string | null) => {
      const items = await this.getListWithKey(key, token, limit);
      return items;
    });
    return items;
  }

  /**
   * サブキーで取得
   * @param key キー
   * @param nextToken 次のトークン
   * @param limit 上限
   * @returns リスト
   */
  async getListWithSubKey(key: string, nextToken: string | null, limit = 25) {
    const result = await this.retryGetEntitiesWithSubKey(
      async () => {
        const client = await this.cognitoApi.getGraphQLClient();
        const sdk = getSdk(client);
        const response = await sdk.GetEntitiesWithSubKey({
          entityName: this.entityName,
          key,
          limit,
          nextToken,
        });
        return response;
      },
      this.entityName,
    );
    if (result.errorObj) {
      throw handleError(result.errorObj);
    }
    const resultEntity = result.response?.getEntitiesWithSubKey;
    if (!resultEntity || !resultEntity.items) {
      throw Error(`Error in ${this.entityName}/getListWithSubKey: not found property`);
    }
    const items = resultEntity.items.map(
      (v) => this.createFromEntityType(v),
    );
    return {
      /** 次のトークン */
      nextToken: resultEntity.nextToken || '',
      /** アイテムリスト */
      items,
    };
  }

  /**
   * サブキーで全リストを取得
   * @param key キー
   * @param limit 上限
   * @returns リスト
   */
  async getAllListWithSubKey(key: string, limit = 25) {
    const items = this.getAllData(async (token: string | null) => {
      const items = await this.getListWithSubKey(key, token, limit);
      return items;
    });
    return items;
  }

  /**
   * Entity名
   */
  protected abstract get entityName(): EntityName;

  /**
   * DBデータをEntityに変換
   * @param props DBデータ
   * @returns Entity
   */
  protected abstract createFromEntityType: (props: EntityDataPubType) => E;

  /**
   * CreateEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryCreateEntity(func: () =>
    Promise<CreateEntityMutation>, entityName: string) {
    return retry(async (_bail) => funcWrap<CreateEntityMutation>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/create: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * UpdateEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryUpdateEntity(func: () =>
    Promise<UpdateEntityMutation>, entityName: string) {
    return retry(async (_bail) => funcWrap<UpdateEntityMutation>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/update: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * DeleteEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryDeleteEntity(func: () =>
    Promise<DeleteEntityMutation>, entityName: string) {
    return retry(async (_bail) => funcWrap<DeleteEntityMutation>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/delete: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetEntity(func: () => Promise<GetEntityQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetEntityQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/get: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetEntitiesのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetEntities(func: () => Promise<GetEntitiesQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetEntitiesQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getList: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetEntitoesWithKeyのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetEntitiesWithKey(func: () =>
    Promise<GetEntitiesWithKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetEntitiesWithKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetEntitiesWithSubKey
   * @param func func
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetEntitiesWithSubKey(func: () =>
    Promise<GetEntitiesWithSubKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetEntitiesWithSubKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithSubKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetDataEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetDataEntity(func: () =>
    Promise<GetDataEntityQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetDataEntityQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/get: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetDataEntities
   * @param func func
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetDataEntities(func: () =>
    Promise<GetDataEntitiesQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetDataEntitiesQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getList: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetDataEntitiesWithKey
   * @param func func
   * @param entityName entity名
   * @returns 結果
   */
  protected async retryGetDataEntitiesWithKey(func: () =>
    Promise<GetDataEntitiesWithKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetDataEntitiesWithKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetDataEntitiesWithSubKey
   * @param func func
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetDataEntitiesWithSubKey(func: () =>
    Promise<GetDataEntitiesWithSubKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetDataEntitiesWithSubKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithSubKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetPubEntityのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPubEntity(func: () =>
    Promise<GetPubEntityQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPubEntityQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getPub: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * GetPubEntitiesのリトライ
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPubEntities(func: () =>
    Promise<GetPubEntitiesQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPubEntitiesQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getPubList: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPubEntitiesWithKey
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPubEntitiesWithKey(func: () =>
    Promise<GetPubEntitiesWithKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPubEntitiesWithKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getPubListWithKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPubEntitiesWithSubKey
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPubEntitiesWithSubKey(func: () =>
    Promise<GetPubEntitiesWithSubKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPubEntitiesWithSubKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getPubListWithSubKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPublicEntity
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPublicEntity(func: () =>
    Promise<GetPublicEntityQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPublicEntityQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/get: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPublicEntities
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPublicEntities(func: () =>
    Promise<GetPublicEntitiesQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPublicEntitiesQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getList: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPublicEntitiesWithKey
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPublicEntitiesWithKey(func: () =>
    Promise<GetPublicEntitiesWithKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPublicEntitiesWithKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }

  /**
   * retryGetPublicEntitiesWithSubKey
   * @param func 関数
   * @param entityName Entity名
   * @returns 結果
   */
  protected async retryGetPublicEntitiesWithSubKey(func: () =>
    Promise<GetPublicEntitiesWithSubKeyQuery>, entityName: string) {
    return retry(async (_bail) => funcWrap<GetPublicEntitiesWithSubKeyQuery>(func), {
      retries,
      onRetry: (err, count) => {
        api.logger.warn(`Retry ${entityName}/getListWithSubKey: ${count}:${err.name}:${err.message}`);
      },
    });
  }
}
